import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';

import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import type { AttributeOption, ResolvedField } from 'types/tpo/reportSetup';
import { sendNotification } from 'utils/helperMethods';
import { PAGE_LIMIT } from 'utils/tpoReportSetupHelpers';

type AttributeSelectFieldProps = {
    field: ResolvedField;
    value: any;
    disabled: boolean;
    multi: boolean;
    onChange: (val: any) => void;
    buildFilters: (skipFieldKey: string) => Record<string, any>;
    selectAllConfig?: { enabled: boolean; maxSelections?: number };
    selectedFilter: ResolvedField | null;
    onFieldOpen: (field: ResolvedField) => void;
    viewMode?: boolean;
    presetOptions?: AttributeOption[];
};

const AttributeSelectField: FC<AttributeSelectFieldProps> = ({
    field,
    value,
    disabled,
    multi,
    onChange,
    buildFilters,
    selectAllConfig,
    selectedFilter,
    onFieldOpen,
    viewMode = false,
    presetOptions,
}) => {
    const [offset, setOffset] = useState(0);
    const [searchKey, setSearchKey] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [list, setList] = useState<AttributeOption[]>(() => (viewMode && presetOptions?.length ? presetOptions : []));
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!viewMode) return;
        setList(presetOptions ?? []);
    }, [viewMode, presetOptions]);

    const isActive = !viewMode && selectedFilter?.key === field.key;

    const filters = useMemo(() => (isActive ? buildFilters(field.key) : null), [selectedFilter, field.key]);

    const variables = useMemo(
        () => ({
            attribute_type: field.key,
            dimension: field.dimension,
            attribute_config_id: field.attributeConfigId,
            limit: PAGE_LIMIT,
            offset,
            search_key: searchKey,
            filters,
        }),
        [field.key, field.dimension, field.attributeConfigId, offset, searchKey, filters],
    );

    // Reset paging when this field becomes active again (new selectedFilter identity) or
    // when the user commits a new search via Enter.
    useEffect(() => {
        setOffset(0);
    }, [selectedFilter, searchKey]);

    const { loading: fetchLoading, error: fetchError } = useCommonApiHandler({
        method: 'post',
        url: tradePromoOptimizationUrls.getReportsFilterAttributeListUrl,
        queryKey: ['tpo-reports-filter-attribute-list', field.key, variables],
        variables,
        queryConfig: {
            // Never fetch in view mode — the dropdown is disabled and `list` is
            // seeded from the saved report's filters instead.
            enabled: !viewMode && isActive && filters != null && !!field.attributeConfigId && !!field.dimension,
        },
        parserFunction: (res: any) => {
            const rows: AttributeOption[] = (res?.attributes ?? []).map((a: any) => ({
                label: a.attributeDesc,
                value: a.attributeExId,
                attributeType: a.attributeType,
                dimension: a.dimension,
                attributeConfigId: a.attributeConfigId,
            }));
            if (offset > 0) {
                setList((prev) => [...prev, ...rows]);
            } else {
                setList(rows);
                setCount(res?.total_count ?? 0);
            }
            return { data: rows, count: res?.total_count ?? 0 };
        },
    });

    const onInfiniteScrollOnNextMemoised = useCallback(() => {
        if (fetchLoading) return;
        if (list.length >= count) return;
        setOffset((o) => o + PAGE_LIMIT);
    }, [fetchLoading, list.length, count]);

    // Select All target (capped by maxSelections) — applies to currently loaded options.
    const allValues = list.map((o) => o.value);
    const cap = selectAllConfig?.maxSelections ?? allValues.length;
    const selectAllTarget = allValues.slice(0, cap);
    const isAllSelected = Array.isArray(value) && selectAllTarget.length > 0 && value.length === selectAllTarget.length;

    const mergedOptions = useMemo(() => {
        let merged: AttributeOption[];
        if (!presetOptions?.length) {
            merged = list;
        } else {
            const seenValues = new Set<string | number>(list.map((option) => option.value));
            const extraPresets = presetOptions.filter((option) => !seenValues.has(option.value));
            merged = extraPresets.length ? [...list, ...extraPresets] : list;
        }

        // plan version labels to uppercase
        if (field.key === 'plan_version') {
            return merged.map((option) => ({ ...option, label: String(option.label ?? '').toUpperCase() }));
        }
        return merged;
    }, [list, presetOptions, field.key]);

    // page-0 refetch in flight with stale rows still rendered — drives the dimming overlay
    const isRefetching = fetchLoading && offset === 0 && list.length > 0;

    // first page-0 fetch with empty list — swaps stale preset rows for a clean loader (edit mode)
    const isFirstFetching = fetchLoading && offset === 0 && list.length === 0;

    return (
        <SelectWithInfiniteScroller
            hasMore={!viewMode && list.length < count}
            onInfiniteScrollOnNextMemoised={onInfiniteScrollOnNextMemoised}
            loading={!viewMode && fetchLoading}
            className="w-full"
            style={{ width: '100%' }}
            mode={multi ? 'multiple' : undefined}
            maxTagCount={multi ? 'responsive' : undefined}
            placeholder={field.placeholder ?? 'Select'}
            value={value ?? (multi ? [] : undefined)}
            options={mergedOptions}
            showArrow
            showSearch={!viewMode}
            allowClear={!viewMode}
            autoClearSearchValue={false}
            searchValue={viewMode ? '' : searchInput}
            disabled={viewMode ? false : disabled}
            removeIcon={viewMode ? null : undefined}
            filterOption={false}
            defaultActiveFirstOption={false}
            onVisibilityChange={(open: boolean) => {
                if (viewMode) return;
                if (open) onFieldOpen(field);
            }}
            onSearch={viewMode ? undefined : (v: string) => setSearchInput(v)}
            onInputKeyDown={
                viewMode
                    ? undefined
                    : (e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === 'Enter') {
                              const val = (e.target as HTMLInputElement)?.value ?? '';
                              setSearchKey(val);
                          }
                      }
            }
            notFoundContent={
                fetchLoading ? (
                    <div className="p-3">
                        <Loader />
                    </div>
                ) : fetchError ? (
                    <ErrorBox msg={typeof fetchError === 'string' ? fetchError : 'Failed to load'} />
                ) : viewMode ? (
                    <div className="text-fontGreyAlt font-xs py-4 text-center">Not Selected</div>
                ) : (
                    <ErrorBox msg="No Data" />
                )
            }
            dropdownRender={(menu) => (
                <div className="relative">
                    {!viewMode && multi && selectAllConfig?.enabled && selectAllTarget.length > 0 && !isRefetching && (
                        <div
                            className="px-3 py-2 cursor-pointer border-b border-backgroundGreyAlt6 text-violetAlt font-xs"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onChange(isAllSelected ? [] : selectAllTarget)}
                        >
                            {isAllSelected ? 'Deselect All' : 'Select All'}
                        </div>
                    )}
                    {isFirstFetching ? (
                        <div className="p-3 flex justify-center">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            <div className={isRefetching ? 'opacity-40 pointer-events-none' : ''}>{menu}</div>
                            {isRefetching && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Spin />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            onChange={(val: any) => {
                if (viewMode) return;
                if (
                    multi &&
                    selectAllConfig?.maxSelections &&
                    Array.isArray(val) &&
                    val.length > selectAllConfig.maxSelections
                ) {
                    sendNotification(
                        'info',
                        `You can select up to ${selectAllConfig.maxSelections} ${field.label.toLowerCase()}.`,
                    );
                    onChange(val.slice(0, selectAllConfig.maxSelections));
                    return;
                }
                onChange(val);
            }}
        />
    );
};

export default AttributeSelectField;
